import React from 'react';
import { Link } from 'react-router-dom';
import { Meeting } from '../../types';
import { formatDatePath } from '../../utils';

interface MeetingTileProps {
  meeting: Meeting;
  className?: string;
}

export const MeetingTile: React.FC<MeetingTileProps> = ({ meeting, className = '' }) => {
  // Get performance metrics from the performanceImproved data
  const getPerformanceMetrics = () => {
    if (!meeting.performanceImproved) {
      return {
        totalRaces: 0,
        totalWinners: 0,
        winnerPercentage: 0,
        hasResults: false
      };
    }
    
    const perf = meeting.performanceImproved;
    
    // Calculate total winners (sum of all pick types)
    const totalWinners = 
      perf.top_1_winners + 
      perf.top_2_winners + 
      perf.top_3_winners + 
      perf.value_winners;
    
    const totalRaces = perf.total_races;
    const winnerPercentage = totalRaces > 0 ? (totalWinners / totalRaces) * 100 : 0;
    
    return {
      totalRaces,
      totalWinners,
      winnerPercentage,
      hasResults: true
    };
  };

  const stats = getPerformanceMetrics();
  
  // Get percentage-based background style similar to the full MeetingCard
  const getBackgroundStyle = () => {
    if (!stats.hasResults) {
      return {};
    }
    
    const greenPercentage = Math.min(100, Math.max(0, stats.winnerPercentage));
    
    return {
      backgroundImage: `
        linear-gradient(45deg, 
          rgba(39, 174, 96, 0.1) 0%, 
          rgba(39, 174, 96, 0.1) ${greenPercentage}%, 
          rgba(244, 67, 54, 0.1) ${greenPercentage}%, 
          rgba(244, 67, 54, 0.1) 100%
        )
      `
    };
  };

  // Check if the meeting is abandoned
  const isAbandoned = meeting.trackConditions?.abandoned === true;

  return (
    <Link
      to="/register"
      className={`meeting-tile ${className} cursor-pointer block hover:scale-105 transition-transform`}
    >
      <div
        className="meeting-tile-header"
        style={getBackgroundStyle()}
      >
        <div className="meeting-tile-info">
          <h3 className="meeting-tile-name">
            {meeting.is_private && (
              <svg className="w-3 h-3 inline mr-1 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            )}
            {meeting.track.name}
          </h3>
        </div>
        
        {stats.hasResults && (
          <div className="meeting-tile-stats">
            <div className="stat-compact">
              <span className="stat-value">Total Winners {stats.totalWinners}/{stats.totalRaces}</span>
            </div>
            <div className="performance-indicator">
              <span className="text-xs text-gray-600">
                {stats.winnerPercentage.toFixed(0)}% success
              </span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

export default MeetingTile;